/**
 * Post-ELK symmetrization helpers for branching layouts.
 *
 * ⚠️  HERE BE DRAGONS ⚠️
 *
 * This file contains the post-layout edge-routing helpers that run after ELK
 * computes node positions. ELK owns positioning now (it produces balanced,
 * rank-aligned branch lanes), but its native per-edge orthogonal router merges
 * convergence edges asymmetrically — so this module:
 *
 *   - Computes per-branch subgraph widths and horizontal lane centers
 *   - Generates orthogonal edge sections (split edges + symmetric convergence edges)
 *   - Updates ELK port positions to match the lane centers
 *
 * These functions are called exclusively by elk-pipeline.ts as part of the
 * symmetrizeSplitLayout pass. They are not intended to be used directly by
 * other modules.
 *
 * WHEN TO MODIFY THIS FILE:
 *   - Fixing visual symmetry bugs in branching layouts
 *   - Adjusting branch lane spacing or edge routing geometry
 *   - Supporting new branching node types with different layout needs
 *
 * WHEN NOT TO MODIFY THIS FILE:
 *   - Changing ELK algorithm options → update elk-config.ts
 *   - Changing how the ELK graph is constructed → update elk-pipeline.ts
 *   - Changing edge/node rendering → update the edge-types/ or node-types/ components
 */
import { LAYOUT_CONSTANTS, SPLIT_LAYOUT_CONSTANTS } from './elk-config'
import { BRANCH_HANDLE_PREFIX } from '../constants'
import type { IBranch } from '@pushly/models/lib/structs/journeys'
import { NodePosition, PortPosition, ElkEdgeSection } from './elk-types'
import { JourneyNode, JourneyEdge } from '../types/journey-nodes'
import { getNodeLayoutDimensions } from './node-dimensions'
import { isBranchingNode, getBranchingNodeData } from '../utils/graph/traversal'
import { getNodeOutStepId } from '../utils/graph/traversal'

// region Interfaces

/**
 * Information about a single branch of a branching node.
 *
 * `spineNodeIds` — the linear chain of nodes along the branch's main path
 *   (branching node → out_step_id → ... → join_step_id). When a spine node
 *   is itself a branching node, the walk jumps through its join_step_id.
 *   Used for: convergence edge lookup (last spine node), first-node targeting.
 *
 * `scopeNodeIds` — ALL nodes owned by this branch, including spine nodes
 *   plus every node inside nested branching scopes. Used for: bounding box
 *   computation, shifting, vertical centering, region membership checks.
 */
export interface BranchInfo {
    variantIdx: number
    variant: { id: string; name: string; percentage: number; out_step_id: string | number | null }
    spineNodeIds: string[]
    scopeNodeIds: string[]
}

/** Map graph branch records onto the variant shape collectBranchSubgraphs expects. */
function toVariantShape(branches: IBranch[]): BranchInfo['variant'][] {
    return branches.map(b => ({
        id: b.key,
        name: b.label,
        percentage: b.kind === 'variant' ? b.percentage : 0,
        out_step_id: b.out_step_id,
    }))
}

export interface ConvergeContext {
    baseConvergeY: number
    joinTopCenterX: number
    joinTopY: number
}

/**
 * Pre-computed context for a single branching node's edge-routing pass.
 * Computed once per split so the split-edge and convergence-edge generators
 * share the same geometry without recomputing it.
 */
export interface SplitContext {
    splitNode: JourneyNode
    splitPos: NodePosition
    splitWidth: number
    splitHeight: number
    splitCenterX: number
    joinStepId: string
    branches: BranchInfo[]
    nodeMap: Map<string, JourneyNode>
    variantShaped: BranchInfo['variant'][]
}

// endregion

// region Build SplitContext

/**
 * Build the shared SplitContext for a branching node.
 * Returns null if the node is not a valid branching node with ≥2 branches.
 */
export function buildSplitContext(
    splitNode: JourneyNode,
    nodes: JourneyNode[],
    positions: Map<string, NodePosition>,
): SplitContext | null {
    if (!isBranchingNode(splitNode)) return null
    const branchingData = getBranchingNodeData(splitNode)
    if (!branchingData) return null
    const joinStepId = branchingData.join_step_id ? String(branchingData.join_step_id) : null
    if (!joinStepId || branchingData.branches.length < 2) return null
    const splitPos = positions.get(splitNode.id)
    if (!splitPos) return null

    const { width: splitWidth, height: splitHeight } = getNodeLayoutDimensions(splitNode)
    const splitCenterX = splitPos.x + splitWidth / 2
    const nodeMap = new Map(nodes.map(n => [n.id, n]))

    const variantShaped = toVariantShape(branchingData.branches)

    const branches = collectBranchSubgraphs(variantShaped, nodeMap, joinStepId)
    if (branches.length < 2) return null

    return {
        splitNode,
        splitPos,
        splitWidth,
        splitHeight,
        splitCenterX,
        joinStepId,
        branches,
        nodeMap,
        variantShaped,
    }
}

// endregion

// region Collect branch subgraphs

/**
 * Recursively collect all nodes within a nested branching node's scope.
 * Walks each branch from its out_step_id up to (but excluding) the nested join target.
 */
function collectNestedScopeNodes(
    branchingNode: JourneyNode,
    nodeMap: Map<string, JourneyNode>,
): string[] {
    const nestedData = getBranchingNodeData(branchingNode)
    if (!nestedData) return []

    const nestedJoinId = nestedData.join_step_id != null ? String(nestedData.join_step_id) : null
    const result: string[] = []
    const visited = new Set<string>()

    for (const branch of nestedData.branches) {
        const outId = branch.out_step_id ? String(branch.out_step_id) : null
        if (!outId || outId === nestedJoinId) continue

        let currentId: string | null = outId
        while (currentId && currentId !== nestedJoinId && !visited.has(currentId)) {
            visited.add(currentId)
            result.push(currentId)
            const currentNode = nodeMap.get(currentId)
            if (!currentNode) break

            if (isBranchingNode(currentNode)) {
                result.push(...collectNestedScopeNodes(currentNode, nodeMap))
                const deepData = getBranchingNodeData(currentNode)
                if (deepData && deepData.join_step_id != null) {
                    currentId = String(deepData.join_step_id)
                    continue
                }
                break
            }

            const nextId = getNodeOutStepId(currentNode)
            currentId = nextId
        }
    }

    return result
}

/**
 * Walk each variant path to collect intermediate nodes between split and join.
 * Returns one BranchInfo per variant, in variant order.
 *
 * Produces two node lists per branch:
 *   - `spineNodeIds`: the linear chain (branching nodes are included but their
 *     internal nodes are not; the walk jumps through join_step_id)
 *   - `scopeNodeIds`: spine nodes PLUS all nodes inside nested branching scopes
 */
export function collectBranchSubgraphs(
    variants: BranchInfo['variant'][],
    nodeMap: Map<string, JourneyNode>,
    joinStepId: string,
): BranchInfo[] {
    const branches: BranchInfo[] = []

    for (let vi = 0; vi < variants.length; vi++) {
        const variant = variants[vi]
        const outId = variant.out_step_id ? String(variant.out_step_id) : null

        if (!outId || outId === joinStepId) {
            branches.push({ variantIdx: vi, variant, spineNodeIds: [], scopeNodeIds: [] })
            continue
        }

        const spineNodeIds: string[] = []
        const scopeNodeIds: string[] = []
        let currentId: string | null = outId
        while (currentId && currentId !== joinStepId) {
            spineNodeIds.push(currentId)
            scopeNodeIds.push(currentId)
            const currentNode = nodeMap.get(currentId)
            if (!currentNode) break

            if (isBranchingNode(currentNode)) {
                const nestedData = getBranchingNodeData(currentNode)
                if (nestedData && nestedData.join_step_id != null) {
                    const nestedScope = collectNestedScopeNodes(currentNode, nodeMap)
                    scopeNodeIds.push(...nestedScope)
                    currentId = String(nestedData.join_step_id)
                    continue
                }
                break
            }

            currentId = getNodeOutStepId(currentNode)
        }
        branches.push({ variantIdx: vi, variant, spineNodeIds, scopeNodeIds })
    }

    return branches
}

// endregion

// region Compute branch widths

/**
 * Compute each branch's bounding-box half-width from scopeNodeIds.
 * Empty branches return MIN_NODE_WIDTH / 2.
 * Non-empty branches return (maxX - minX) / 2 of their bounding box.
 *
 * For branches containing nested branching nodes, the width also accounts
 * for the lane spread of those nested nodes (even when their branches are
 * empty and have no scope nodes). This prevents adjacent branches from
 * different conditions from overlapping in the visual space occupied by
 * empty-branch edges and labels.
 */
export function computeBranchWidths(
    branches: BranchInfo[],
    nodeMap: Map<string, JourneyNode>,
    positions: Map<string, NodePosition>,
): number[] {
    const branchHalfWidths: number[] = []

    for (const branch of branches) {
        if (branch.scopeNodeIds.length === 0) {
            branchHalfWidths.push(LAYOUT_CONSTANTS.MIN_NODE_WIDTH / 2)
            continue
        }

        // Standard bounding box from node positions.
        let minX = Infinity
        let maxX = -Infinity
        for (const nodeId of branch.scopeNodeIds) {
            const node = nodeMap.get(nodeId)
            const { width: w } = getNodeLayoutDimensions(node)
            const pos = positions.get(nodeId)
            if (pos) {
                minX = Math.min(minX, pos.x)
                maxX = Math.max(maxX, pos.x + w)
            }
        }

        // For nested branching nodes, ensure the width accounts for their
        // full visual extent including edge lane paths. ELK has already placed
        // the nested scope nodes, so we recompute their lane centers to get the
        // actual visual span. This is necessary because empty branches have no
        // scope nodes but their edge paths fan out to lane positions that extend
        // beyond node bounding boxes.
        for (const nodeId of branch.scopeNodeIds) {
            const node = nodeMap.get(nodeId)
            if (!node || !isBranchingNode(node)) continue
            const brData = getBranchingNodeData(node)
            if (!brData || brData.branches.length < 2) continue

            const nodePos = positions.get(nodeId)
            if (!nodePos) continue
            const { width: nodeW } = getNodeLayoutDimensions(node)
            const nodeCenterX = nodePos.x + nodeW / 2

            // Recompute the nested split's lane centers with the same
            // computeLaneCenters logic the edge-routing pass uses. ELK has
            // already placed the nested scope nodes at their final positions.
            const nestedJoinId = brData.join_step_id != null ? String(brData.join_step_id) : null
            const nestedVariants = toVariantShape(brData.branches)
            const nestedBranches = nestedJoinId
                ? collectBranchSubgraphs(nestedVariants, nodeMap, nestedJoinId)
                : []

            const nestedHalfWidths = computeBranchWidths(nestedBranches, nodeMap, positions)
            const nestedAnyHasNodes = nestedBranches.some(nb => nb.scopeNodeIds.length > 0)
            const nestedLaneCenters = computeLaneCenters(
                nestedBranches.length, nodeCenterX, nodeW,
                nestedAnyHasNodes, nestedHalfWidths,
            )

            // Expand bounding box to cover all nested lane centers
            for (const lc of nestedLaneCenters) {
                minX = Math.min(minX, lc - LAYOUT_CONSTANTS.MIN_NODE_WIDTH / 2)
                maxX = Math.max(maxX, lc + LAYOUT_CONSTANTS.MIN_NODE_WIDTH / 2)
            }
        }

        const halfW = minX < Infinity ? (maxX - minX) / 2 : LAYOUT_CONSTANTS.MIN_NODE_WIDTH / 2
        branchHalfWidths.push(halfW)
    }

    return branchHalfWidths
}

// endregion

// region Compute lane centers

/**
 * Compute symmetric lane center X-coordinates around splitCenterX.
 *
 * For 2 empty variants, uses a compact gap (splitWidth * 0.6).
 * For branches with content, computes adaptive spacing based on actual
 * branch widths so that nested branching nodes don't overlap.
 * The minimum gap between adjacent lanes is LANE_OFFSET.
 */
export function computeLaneCenters(
    branchCount: number,
    splitCenterX: number,
    splitWidth: number,
    anyBranchHasNodes: boolean,
    branchHalfWidths?: number[],
): number[] {
    const { LANE_OFFSET, MIN_LANE_GAP, EMPTY_LANE_GAP_FACTOR } = SPLIT_LAYOUT_CONSTANTS

    if (!anyBranchHasNodes && branchCount <= 2) {
        const gap = splitWidth * EMPTY_LANE_GAP_FACTOR
        const laneCenters: number[] = []
        for (let i = 0; i < branchCount; i++) {
            const offset = (i - (branchCount - 1) / 2) * gap
            laneCenters.push(splitCenterX + offset)
        }
        return laneCenters
    }

    const halfWidths = branchHalfWidths && branchHalfWidths.length === branchCount
        ? branchHalfWidths
        : Array(branchCount).fill(LAYOUT_CONSTANTS.MIN_NODE_WIDTH / 2)

    const gaps: number[] = []
    for (let i = 0; i < branchCount - 1; i++) {
        const contentGap = halfWidths[i] + halfWidths[i + 1] + MIN_LANE_GAP
        gaps.push(Math.max(LANE_OFFSET, contentGap))
    }

    const laneCenters: number[] = [0]
    for (let i = 0; i < gaps.length; i++) {
        laneCenters.push(laneCenters[i] + gaps[i])
    }

    const totalSpan = laneCenters[laneCenters.length - 1] - laneCenters[0]
    const leftEdge = splitCenterX - totalSpan / 2
    for (let i = 0; i < laneCenters.length; i++) {
        laneCenters[i] = leftEdge + laneCenters[i]
    }

    return laneCenters
}

// endregion

// region Clear split-region edge routing

/**
 * Remove stale ELK routing for any edge whose source AND target are both
 * in the split region. Uses scopeNodeIds for region membership.
 */
export function clearSplitRegionEdgeRouting(
    splitNodeId: string,
    joinStepId: string,
    branches: BranchInfo[],
    edges: JourneyEdge[],
    edgeRouting: Map<string, ElkEdgeSection[]>,
): void {
    const splitRegionNodeIds = new Set<string>([splitNodeId, joinStepId])
    for (const branch of branches) {
        for (const nodeId of branch.scopeNodeIds) {
            splitRegionNodeIds.add(nodeId)
        }
    }

    for (const edge of edges) {
        if (splitRegionNodeIds.has(edge.source) && splitRegionNodeIds.has(edge.target)) {
            edgeRouting.delete(edge.id)
        }
    }
}

// endregion

// region Update port positions

/**
 * Generate port positions for each variant, mapping lane centers to port X
 * coordinates relative to the split node. Port X is clamped to [10%, 90%]
 * of splitWidth. Port Y equals splitHeight (bottom of split node).
 */
export function updatePortPositions(
    variants: BranchInfo['variant'][],
    laneCenters: number[],
    splitPos: NodePosition,
    splitWidth: number,
    splitHeight: number,
): PortPosition[] {
    const { PORT_X_CLAMP_MIN, PORT_X_CLAMP_MAX } = SPLIT_LAYOUT_CONSTANTS
    return variants.map((v, idx) => {
        const laneCenter = laneCenters[idx] ?? (splitPos.x + splitWidth / 2)
        const relativeX = laneCenter - splitPos.x
        const clampedX = Math.max(splitWidth * PORT_X_CLAMP_MIN, Math.min(splitWidth * PORT_X_CLAMP_MAX, relativeX))
        return { portId: `${BRANCH_HANDLE_PREFIX}${v.id}`, x: clampedX, y: splitHeight }
    })
}

// endregion

// region Converge context computation

/**
 * Compute the shared converge context used by both split edge and convergence
 * edge section generators. Uses scopeNodeIds for depth computation.
 *
 * The converge Y is a fixed gap above the join target, ensuring consistent
 * vertical spacing regardless of branch depth or nesting. This means every
 * split's edges share the same convergence pattern: horizontal at convergeY,
 * then a fixed-length vertical drop into the join target.
 */
export function computeConvergeContext(
    branches: BranchInfo[],
    positions: Map<string, NodePosition>,
    nodeMap: Map<string, JourneyNode>,
    joinStepId: string,
): ConvergeContext {
    const joinNode = nodeMap.get(joinStepId)
    const { width: joinWidth } = getNodeLayoutDimensions(joinNode)

    const updatedJoinPos = positions.get(joinStepId)!
    const joinTopCenterX = updatedJoinPos.x + joinWidth / 2
    const joinTopY = updatedJoinPos.y

    const baseConvergeY = joinTopY - SPLIT_LAYOUT_CONSTANTS.CONVERGE_GAP

    return { baseConvergeY, joinTopCenterX, joinTopY }
}

// endregion

// region Generate split edge sections

/**
 * Generate orthogonal edge sections for split edges (split node → first
 * variant node or join target for empty variants).
 *
 * Uses spineNodeIds[0] for the first target node in a branch (the first
 * node on the linear path, not a nested internal node).
 *
 * Empty variant edges get 4 bend points (port → bendY → lane → convergeY → center).
 * Non-empty variant edges get 0 or 2 bend points depending on alignment.
 * All bend points form orthogonal (Manhattan) paths.
 *
 * Mutates `edgeRouting` in place.
 */
export function generateBranchEdgeSections(
    branches: BranchInfo[],
    ports: PortPosition[],
    splitNode: JourneyNode,
    edges: JourneyEdge[],
    positions: Map<string, NodePosition>,
    nodeMap: Map<string, JourneyNode>,
    laneCenters: number[],
    splitPos: NodePosition,
    convergeCtx: ConvergeContext,
    edgeRouting: Map<string, ElkEdgeSection[]>,
): void {
    const { BEND_GAP, ALIGN_TOLERANCE } = SPLIT_LAYOUT_CONSTANTS
    const { baseConvergeY, joinTopCenterX, joinTopY } = convergeCtx

    for (let bi = 0; bi < branches.length; bi++) {
        const branch = branches[bi]
        const laneCenterX = laneCenters[bi]
        const port = ports[bi]
        const portAbsX = splitPos.x + port.x
        const portAbsY = splitPos.y + port.y

        const branchEdge = edges.find(e =>
            e.type === 'branch-edge' &&
            e.source === splitNode.id &&
            e.data?.branchKey === branch.variant.id,
        )
        if (!branchEdge) continue

        // Use spineNodeIds for first target — this is the first node on the
        // linear path, not a nested internal node.
        const targetNodeId = branch.spineNodeIds.length > 0 ? branch.spineNodeIds[0] : branchEdge.target
        const targetPos = positions.get(targetNodeId)
        if (!targetPos) continue
        const targetNode = nodeMap.get(targetNodeId)
        const { width: targetWidth } = getNodeLayoutDimensions(targetNode)
        const targetTopCenterX = targetPos.x + targetWidth / 2
        const targetTopY = targetPos.y

        const bendY = portAbsY + BEND_GAP
        const bendPoints: { x: number; y: number }[] = []
        const targetIsInLane = branch.spineNodeIds.length > 0

        if (targetIsInLane) {
            if (Math.abs(portAbsX - targetTopCenterX) < ALIGN_TOLERANCE) {
                // Nearly vertically aligned — straight down
            } else {
                bendPoints.push({ x: portAbsX, y: bendY })
                bendPoints.push({ x: targetTopCenterX, y: bendY })
            }
        } else {
            // Empty branch: route from port → lane center → converge level → join target.
            bendPoints.push({ x: portAbsX, y: bendY })
            bendPoints.push({ x: laneCenterX, y: bendY })
            bendPoints.push({ x: laneCenterX, y: baseConvergeY })
            bendPoints.push({ x: joinTopCenterX, y: baseConvergeY })
        }

        const section: ElkEdgeSection = {
            id: `${branchEdge.id}_s0`,
            startPoint: { x: portAbsX, y: portAbsY },
            endPoint: { x: targetTopCenterX, y: targetTopY },
            bendPoints: bendPoints.length > 0 ? bendPoints : undefined,
        }
        edgeRouting.set(branchEdge.id, [section])
    }
}

// endregion

// region Generate convergence edge sections

/**
 * Generate orthogonal edge sections for convergence edges (last variant
 * node → join target). Only processes branches with intermediate nodes.
 *
 * Uses spineNodeIds (not scopeNodeIds) for last-node lookup. The last
 * spine node is the actual final node before the join target — not a
 * deeply nested node inside a condition's sub-branch.
 *
 * Mutates `edgeRouting` in place.
 */
export function generateConvergenceEdgeSections(
    branches: BranchInfo[],
    edges: JourneyEdge[],
    positions: Map<string, NodePosition>,
    nodeMap: Map<string, JourneyNode>,
    convergeCtx: ConvergeContext,
    joinStepId: string,
    edgeRouting: Map<string, ElkEdgeSection[]>,
): void {
    const { baseConvergeY, joinTopCenterX, joinTopY } = convergeCtx
    const { ALIGN_TOLERANCE } = SPLIT_LAYOUT_CONSTANTS

    for (let bi = 0; bi < branches.length; bi++) {
        const branch = branches[bi]
        if (branch.spineNodeIds.length === 0) continue

        // Use last spine node — the actual final node before the join target.
        const lastNodeId = branch.spineNodeIds[branch.spineNodeIds.length - 1]
        const convergenceEdge = edges.find(e =>
            e.type !== 'branch-edge' &&
            e.source === lastNodeId &&
            e.target === joinStepId,
        )
        if (!convergenceEdge) continue

        const lastNodePos = positions.get(lastNodeId)
        if (!lastNodePos) continue
        const lastNode = nodeMap.get(lastNodeId)
        const { width: lastNodeWidth, height: lastNodeHeight } = getNodeLayoutDimensions(lastNode)
        const srcCenterX = lastNodePos.x + lastNodeWidth / 2
        const srcBottomY = lastNodePos.y + lastNodeHeight

        const convergeY = baseConvergeY
        const convBendPoints: { x: number; y: number }[] = []

        if (Math.abs(srcCenterX - joinTopCenterX) < ALIGN_TOLERANCE) {
            // Nearly vertically aligned — straight down (avoid tiny horizontal jogs
            // that create visible "double line" artifacts)
        } else {
            convBendPoints.push({ x: srcCenterX, y: convergeY })
            convBendPoints.push({ x: joinTopCenterX, y: convergeY })
        }

        const convSection: ElkEdgeSection = {
            id: `${convergenceEdge.id}_s0`,
            startPoint: { x: srcCenterX, y: srcBottomY },
            endPoint: { x: Math.abs(srcCenterX - joinTopCenterX) < ALIGN_TOLERANCE ? srcCenterX : joinTopCenterX, y: joinTopY },
            bendPoints: convBendPoints.length > 0 ? convBendPoints : undefined,
        }
        edgeRouting.set(convergenceEdge.id, [convSection])
    }
}

// endregion
